import { Project, Test } from '@analyzer/client';
import AddIcon from '@mui/icons-material/Add';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useReducer } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjectReadQuery } from '../api/project.js';
import { useSnapshotCreateMutation } from '../api/snapshot.js';
import { useTestListQuery } from '../api/test.js';
import { DeleteTestDialog } from '../components/test/dialogs/delete.js';
import { DisableTestDialog } from '../components/test/dialogs/disable.js';
import { EditTestDialog } from '../components/test/dialogs/edit.js';
import { NewTestDialog } from '../components/test/dialogs/new.js';

type Action =
  | { type: 'OPEN_MODAL'; modal: 'new' | 'edit' | 'delete' | 'disable' }
  | { type: 'CLOSE_MODAL'; modal: 'new' | 'edit' | 'delete' | 'disable' }
  | { type: 'SET_PROJECT'; project: Project }
  | { type: 'SET_TEST'; test: Test };

interface State {
  new: boolean;
  edit: boolean;
  delete: boolean;
  disable: boolean;
  project: Project | undefined;
  test: Test | undefined;
}

const initialState: State = {
  new: false,
  edit: false,
  delete: false,
  disable: false,
  test: undefined,
  project: undefined
};

function modalReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, [action.modal]: true };
    case 'CLOSE_MODAL':
      return { ...state, [action.modal]: false };
    case 'SET_PROJECT':
      return { ...state, project: action.project };
    case 'SET_TEST':
      return { ...state, test: action.test };
    default:
      return state;
  }
}

export function TestsView() {
  const { projectId } = useParams();
  if (!projectId) throw new Error('Missing parameter');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [state, dispatch] = useReducer(modalReducer, initialState);
  const snapshotCreate = useSnapshotCreateMutation('web');

  const { data: tests, isLoading, isError } = useTestListQuery(projectId);
  const { data: project } = useProjectReadQuery(projectId);

  useEffect(() => {
    if (project) {
      dispatch({ type: 'SET_PROJECT', project });
    }
  }, [project]);

  const handleCreateClick = () => {
    dispatch({ type: 'OPEN_MODAL', modal: 'new' });
  };

  const handleEditClick = (test: Test, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch({ type: 'SET_TEST', test });
    dispatch({ type: 'OPEN_MODAL', modal: 'edit' });
  };

  const handleDeleteClick = (test: Test, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch({ type: 'SET_TEST', test });
    dispatch({ type: 'OPEN_MODAL', modal: 'delete' });
  };

  const handleDisableClick = (test: Test, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch({ type: 'SET_TEST', test });
    dispatch({ type: 'OPEN_MODAL', modal: 'disable' });
  };

  const handleSnapshotClick = (test: Test, event: React.MouseEvent) => {
    event.stopPropagation();
    snapshotCreate.mutate(
      { projectId, resourceId: test.id, settings: { recordTrace: true, takeScreenshot: true } },
      {
        onSuccess: () => enqueueSnackbar('Snapshot job submitted successfully', { variant: 'success' }),
        onError: (err) =>
          enqueueSnackbar(
            `Error: Failed to submit snapshot job (${err instanceof Error ? err.message : String(err)})`,
            { variant: 'error' }
          )
      }
    );
  };

  const handleTestClick = (testId: string) => {
    navigate(`/projects/${projectId}/tests/${testId}`);
  };

  if (isLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isError || !tests) {
    return <Alert severity="error">Unable to load tests. Please try again later.</Alert>;
  }

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Tests</Typography>
        <Box>
          <Button variant="contained" color="primary" onClick={handleCreateClick} startIcon={<AddIcon />}>
            Create Test
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tests.map((test) => (
              <TableRow
                key={test.id}
                hover
                onClick={() => handleTestClick(test.id)}
                sx={{ cursor: 'pointer', opacity: test.enabled ? 1 : 0.7 }}
              >
                <TableCell>{test.id}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{test.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LinkIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {test.target}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={test.enabled ? 'Active' : 'Disabled'}
                    color={test.enabled ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Snapshot" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleSnapshotClick(test, e)}
                      aria-label="Snapshot"
                    >
                      <CameraAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit" arrow>
                    <IconButton size="small" onClick={(e) => handleEditClick(test, e)} aria-label="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDeleteClick(test, e)}
                      aria-label="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={test.enabled ? 'Disable' : 'Enable'} arrow>
                    <IconButton
                      size="small"
                      color={test.enabled ? 'warning' : 'success'}
                      onClick={(e) => handleDisableClick(test, e)}
                      aria-label={test.enabled ? 'Disable' : 'Enable'}
                    >
                      <PowerSettingsNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <NewTestDialog
        project={state.project}
        open={state.new}
        onClose={() => dispatch({ type: 'CLOSE_MODAL', modal: 'new' })}
        onConfirm={() => enqueueSnackbar('New test created successfully', { variant: 'success' })}
        onError={(err) =>
          enqueueSnackbar(
            `Error: Unable to create new test (${err instanceof Error ? err.message : String(err)})`,
            { variant: 'error' }
          )
        }
      />

      <EditTestDialog
        project={state.project}
        test={state.test}
        open={state.edit}
        onClose={() => dispatch({ type: 'CLOSE_MODAL', modal: 'edit' })}
        onConfirm={() => enqueueSnackbar('Test updated successfully', { variant: 'success' })}
        onError={(err) =>
          enqueueSnackbar(
            `Error: Failed to update the test (${err instanceof Error ? err.message : String(err)})`,
            { variant: 'error' }
          )
        }
      />

      <DeleteTestDialog
        project={state.project}
        test={state.test}
        open={state.delete}
        onClose={() => dispatch({ type: 'CLOSE_MODAL', modal: 'delete' })}
        onConfirm={() => enqueueSnackbar('Test deleted successfully', { variant: 'success' })}
        onError={(err) =>
          enqueueSnackbar(
            `Error: Failed to delete the test (${err instanceof Error ? err.message : String(err)})`,
            { variant: 'error' }
          )
        }
      />

      <DisableTestDialog
        project={state.project}
        test={state.test}
        open={state.disable}
        onClose={() => dispatch({ type: 'CLOSE_MODAL', modal: 'disable' })}
        onConfirm={() => enqueueSnackbar('Test status updated successfully', { variant: 'success' })}
        onError={(err) =>
          enqueueSnackbar(
            `Error: Unable to update test status (${err instanceof Error ? err.message : String(err)})`,
            { variant: 'error' }
          )
        }
      />
    </Paper>
  );
}
